import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Wrench, ShieldCheck, Truck, Mail, ChevronDown, Phone, Package, Snowflake, Flame, Droplets, Zap, DoorOpen, Sun, Check, Save } from "lucide-react";
import { sendSparePartsRequest } from "@/lib/spare-parts.functions";
import { trackPartsFunnel } from "@/lib/parts-funnel";
import { getMyProfile } from "@/lib/account.functions";
import { PageHeader } from "./fordon";
import { PartAiScanner } from "@/components/PartAiScanner";
import { InlinePartPicker } from "@/components/InlinePartPicker";
import PartPhotoUploader, { type UploadedPhoto } from "@/components/PartPhotoUploader";
import {
  PartPositionPicker,
  formatPositionPicks,
  describePositionPicks,

  POSITION_REQUIRED_CATEGORIES,
  type PositionPick,
} from "@/components/PartPositionPicker";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { RelatedLinks } from "@/components/RelatedLinks";
import { RAPIDO_PART_REFERENCES, type PartTile } from "@/data/rapido-parts";
import { PARTS_BRANDS } from "@/data/reservdelar-brands";
import { PARTS_CATEGORIES } from "@/data/reservdelar-kategorier";

export const Route = createFileRoute("/reservdelar")({
  head: () => ({
    meta: [
      { title: "Reservdelar – Rapido, Dreamer, Itineo, Polar, Dometic, Thetford" },
      {
        name: "description",
        content:
          "Reservdelar till Rapido, Dreamer, Itineo, Polar, Dometic och Thetford. Skicka en tydlig bild – du får pris och leveranstid inom 24 timmar.",
      },

      {
        name: "keywords",
        content:
          "Rapido reservdelar, Dreamer reservdelar, Itineo reservdelar, Polar reservdelar, Dometic reservdelar, Thetford reservdelar, reservdelar husbil, reservdelar husvagn, husbilsdelar, husvagnsdelar, husbilstillbehör",
      },

      { property: "og:title", content: "Reservdelar husbil & husvagn – AK Husbil & Fritid" },
      {
        property: "og:description",
        content:
          "Original & kvalitetsdelar till husbil och husvagn. Svar med pris och leveranstid inom 24 timmar.",
      },
      { property: "og:url", content: "https://www.akhusbilfritid.se/reservdelar" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Reservdelar till husbil och husvagn" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/reservdelar" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Reservdelar till husbil och husvagn",
          serviceType: "Reservdelar husbil och husvagn",
          provider: { "@id": "#organization" },
          areaServed: { "@type": "Country", name: "Sverige" },
          url: "https://www.akhusbilfritid.se/reservdelar",
          brand: [
            "Rapido", "Dreamer", "Itineo", "Polar", "Dometic", "Thetford",
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Reservdelskategorier",
            itemListElement: [
              "Kylskåp & frys", "Värmesystem", "Toalett & sanitet",
              "Vattensystem & pumpar", "Gasolsystem", "El & solpanel",
              "Fönster & takluckor", "Dörr & lås", "Markis",
              "Kaross & plåt", "Chassi, hjul & bromsar", "Inredning & möbler",
              "Belysning", "Draganordning",
            ].map((n) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n } })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Vilka märken kan ni beställa reservdelar till?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Just nu tar vi endast emot förfrågningar för Rapido, Dreamer, Itineo, Polar, Dometic och Thetford — där har vi bäst kontakt och snabbast tillgång till originaldelar. Andra märken kan vi tyvärr inte hantera för tillfället.",
              },
            },
            {
              "@type": "Question",
              name: "Hur lång är leveranstiden på reservdelar?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Delar i lager skickas ofta inom 1–3 arbetsdagar. Beställningsvaror har normalt 3–7 arbetsdagars leveranstid. Vi svarar alltid på din förfrågan inom 24 timmar på vardagar med pris och exakt leveranstid.",
              },
            },
            {
              "@type": "Question",
              name: "Kan ni hitta delar till äldre husbilar och husvagnar?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. Med reg.nr eller chassinummer (VIN) kan vi identifiera rätt del även till äldre modeller. Om originaldelen inte längre tillverkas hjälper vi dig hitta en kompatibel ersättare.",
              },
            },
            {
              "@type": "Question",
              name: "Skickar ni reservdelar i hela Sverige?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja, vi skickar reservdelar till husbil och husvagn över hela Sverige. Du kan också hämta delarna på plats i vår butik i Kalix, Norrbotten.",
              },
            },
            {
              "@type": "Question",
              name: "Vad kostar det att göra en förfrågan?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Förfrågan är helt gratis och utan förpliktelser. Du får pris och leveranstid från oss innan du bestämmer dig.",
              },
            },
            {
              "@type": "Question",
              name: "Kan jag skicka en bild om jag inte vet vad delen heter?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. Använd vår bildigenkänning högst upp på sidan – ladda upp en bild på delen så identifierar vi vad det är och lägger till rätt kategori i förfrågan.",
              },
            },
            {
              "@type": "Question",
              name: "Behöver jag registreringsnummer för att beställa?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Reg.nr eller chassinummer gör att vi träffar rätt del direkt och undviker returer. Har du inget till hands – hör av dig ändå så löser vi det tillsammans.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: ReservdelarPage,
});

const BRANDS = [
  "Rapido", "Dreamer", "Itineo", "Polar", "Dometic", "Thetford",
];

const CATEGORIES = [
  "Kyl / frys",
  "Spis / ugn",
  "Värmesystem (Truma, Alde)",
  "Toalett (Thetford, Dometic)",
  "Vattensystem / pump",
  "Gråvattensystem",
  "Gasolsystem",
  "El & solpanel",
  "Kontrollpaneler & displayer",

  "Batterisystem",
  "Fönster / lucka",
  "Dörr / lås",
  "Markis",
  "Kaross / plåt",
  "Chassi / hjul / bromsar",
  "Inredning / möbler",
  "Tyg / klädsel",
  "Belysning",
  "Draganordning",
  "Övrigt",
];

// Kategorier där små beslag/detaljer (gångjärn, handtag, lås, list, tätning m.m.)
// är svåra att peka ut på en översiktsbild. Här kräver vi en kort beskrivning
// eller kommentar innan formuläret kan skickas – annars blir det ofta fel del.
const HIGH_UNCERTAINTY_CATEGORIES = new Set<string>([
  "Dörr / lås",
  "Inredning / möbler",
  "Tyg / klädsel",
  "Övrigt",
]);


// Alla förfrågningar kräver minst en tydlig bild på delen.


const URGENCY = ["Så snart som möjligt", "Inom en vecka", "Inom en månad", "Ingen brådska"];
const FORM_DRAFT_KEY = "ak-reservdelar-form-draft-v1";

const schema = z.object({
  name: z.string().trim().min(1, "Ange ditt namn").max(200),
  email: z.string().trim().email("Ogiltig e-post").max(320),
  phone: z.string().trim().max(40).optional(),
  brand: z.string().trim().min(1, "Välj märke").max(80),
  model: z.string().trim().max(120).optional(),
  year: z.string().trim().max(10).optional(),
  regNumber: z.string().trim().min(1, "Ange registreringsnummer").max(15),
  chassisNumber: z.string().trim().max(40).optional(),
  mileage: z.string().trim().max(20).optional(),
  categories: z.array(z.string()).optional(),
  urgency: z.string().trim().max(60).optional(),
  message: z.string().trim().max(12000).optional(),
  photoPaths: z.array(z.string()).max(8).optional(),
  wantedParts: z.array(z.string()).max(20).optional(),
  customerNote: z.string().trim().max(6000).optional(),
  positions: z
    .array(
      z.object({
        viewName: z.string().optional(),
        code: z.string().optional(),
        label: z.string().optional(),
        side: z.string().optional(),
        note: z.string().optional(),
        hint: z.string().optional(),
        x: z.number().optional(),
        y: z.number().optional(),
      }),
    )
    .max(12)
    .optional(),
  aiPosition: z.string().trim().max(300).optional(),
  aiQuestions: z.array(z.string()).max(8).optional(),
});


function ReservdelarPage() {
  const send = useServerFn(sendSparePartsRequest);
  const getProfile = useServerFn(getMyProfile);
  const { signedIn } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkedCats, setCheckedCats] = useState<string[]>([]);
  const [brandVal, setBrandVal] = useState<string>("");
  const [modelVal, setModelVal] = useState<string>("");
  useEffect(() => { trackPartsFunnel("form_view"); }, []);
  useEffect(() => { if (checkedCats.length) trackPartsFunnel("category_selected"); }, [checkedCats.length]);
  const [messageVal, setMessageVal] = useState<string>("");
  const [partSelections, setPartSelections] = useState<Record<string, string[]>>({});
  const [partNotes, setPartNotes] = useState<Record<string, string>>({});
  const [positionPicks, setPositionPicks] = useState<PositionPick[]>([]);
  const [fridgeSerial, setFridgeSerial] = useState<string>("");
  const [fridgePhotoName, setFridgePhotoName] = useState<string>("");
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [prefill, setPrefill] = useState<{ name: string; email: string; phone: string } | null>(null);
  const [successRef, setSuccessRef] = useState<string | null>(null);

  // Utkast: spara ifall sidan laddas om
  const [nameVal, setNameVal] = useState("");
  const [emailVal, setEmailVal] = useState("");
  const [phoneVal, setPhoneVal] = useState("");
  const [yearVal, setYearVal] = useState("");
  const [regVal, setRegVal] = useState("");
  const [chassisVal, setChassisVal] = useState("");
  const [mileageVal, setMileageVal] = useState("");
  const [urgencyVal, setUrgencyVal] = useState("");
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  // AI-först: kunden ser bara fotosteget. Kategorier, sprängskiss och referensbilder
  // ligger dolda tills AI:n är osäker eller kunden själv väljer att rätta något.
  type AiPartSummary = {
    label: string;
    category: string;
    zoneLabel: string;
    confidence: number;
    identified: boolean;
  };
  const [aiSummary, setAiSummary] = useState<AiPartSummary[]>([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [describeText, setDescribeText] = useState("");
  const [describing, setDescribing] = useState(false);
  const [describeResult, setDescribeResult] = useState<{
    position: string;
    summary: string;
    questions: string[];
    confidence: number;
  } | null>(null);
  const showManual = manualOpen;

  

  useEffect(() => {
    if (!signedIn) { setPrefill(null); return; }
    getProfile().then((res) => {
      const p = {
        name: res.profile?.display_name || "",
        email: res.email || "",
        phone: res.profile?.phone || "",
      };
      setPrefill(p);
      // Fyll bara tomma fält — utkastet vinner om det finns.
      setNameVal((prev) => prev || p.name);
      setEmailVal((prev) => prev || p.email);
      setPhoneVal((prev) => prev || p.phone);
    }).catch(() => {});
  }, [signedIn, getProfile]);

  // Återställ utkast vid uppstart (localStorage-only, aldrig SSR)
  useEffect(() => {
    let cancelled = false;
    async function loadDraft() {
      try {
        const raw = window.localStorage.getItem(FORM_DRAFT_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== "object") return;
        if (saved.name) setNameVal(saved.name);
        if (saved.email) setEmailVal(saved.email);
        if (saved.phone) setPhoneVal(saved.phone);
        if (saved.brand) setBrandVal(saved.brand);
        if (saved.model) setModelVal(saved.model);
        if (saved.year) setYearVal(saved.year);
        if (saved.regNumber) setRegVal(saved.regNumber);
        if (saved.chassisNumber) setChassisVal(saved.chassisNumber);
        if (saved.mileage) setMileageVal(saved.mileage);
        if (Array.isArray(saved.categories)) setCheckedCats(saved.categories);
        if (saved.urgency) setUrgencyVal(saved.urgency);
        if (saved.message) setMessageVal(saved.message);
        if (saved.partSelections && typeof saved.partSelections === "object") {
          setPartSelections(saved.partSelections);
        }
        if (saved.partNotes && typeof saved.partNotes === "object") {
          setPartNotes(saved.partNotes);
        }
        if (Array.isArray(saved.positionPicks)) setPositionPicks(saved.positionPicks);
        if (saved.fridgeSerial) setFridgeSerial(saved.fridgeSerial);
        if (saved.fridgePhotoName) setFridgePhotoName(saved.fridgePhotoName);
        if (saved.describeText) setDescribeText(saved.describeText);
        if (saved.describeResult) setDescribeResult(saved.describeResult);
        if (Array.isArray(saved.aiSummary)) setAiSummary(saved.aiSummary);
        if (typeof saved.manualOpen === "boolean") setManualOpen(saved.manualOpen);

        const savedPhotos = Array.isArray(saved.photos) ? saved.photos : [];
        if (savedPhotos.length > 0) {
          const restored: UploadedPhoto[] = [];
          for (const p of savedPhotos) {
            if (!p.path || !p.name) continue;
            const { data } = await supabase.storage
              .from("reservdel-foton")
              .createSignedUrl(p.path, 60 * 60);
            restored.push({ path: p.path, name: p.name, preview: data?.signedUrl || "" });
          }
          if (!cancelled) setPhotos(restored);
        }
      } catch {
        // Utkastet är korrupt — ignorerar tyst.
      } finally {
        if (!cancelled) setDraftRestored(true);
      }
    }
    loadDraft();
    return () => { cancelled = true; };
  }, []);

  // Spara utkast automatiskt när något ändras
  useEffect(() => {
    if (!draftRestored) return;
    const timer = setTimeout(() => {
      const snapshot = {
        savedAt: Date.now(),
        name: nameVal,
        email: emailVal,
        phone: phoneVal,
        brand: brandVal,
        model: modelVal,
        year: yearVal,
        regNumber: regVal,
        chassisNumber: chassisVal,
        mileage: mileageVal,
        categories: checkedCats,
        urgency: urgencyVal,
        message: messageVal,
        partSelections,
        partNotes,
        positionPicks,
        fridgeSerial,
        fridgePhotoName,
        describeText,
        describeResult,
        aiSummary,
        manualOpen,
        photos: photos.map(({ path, name }) => ({ path, name })),
      };
      try {
        window.localStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(snapshot));
        setDraftSavedAt(new Date().toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }));
      } catch {
        // Tyst fel vid fullt localStorage.
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [
    draftRestored,
    nameVal,
    emailVal,
    phoneVal,
    brandVal,
    modelVal,
    yearVal,
    regVal,
    chassisVal,
    mileageVal,
    checkedCats,
    urgencyVal,
    messageVal,
    partSelections,
    partNotes,
    positionPicks,
    fridgeSerial,
    fridgePhotoName,
    describeText,
    describeResult,
    aiSummary,
    manualOpen,
    photos,
  ]);

  function toggleCat(cat: string) {
    setCheckedCats((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  }

  function applyAiResult({
    category,
    categories,
    message,
    photos: aiPhotos,
    summary,
  }: {
    category: string;
    categories?: string[];
    message: string;
    photos?: UploadedPhoto[];
    summary?: AiPartSummary[];
  }) {
    // Skriv inte över text kunden redan skrivit – lägg AI-analysen först.
    setMessageVal((prev) => {
      const own = prev.trim();
      return own && !own.startsWith("AI-BILDANALYS") ? `${message}\n\n${own}` : message;
    });

    // Bocka i alla kategorier AI:n hittade som finns i vår lista (kunden kan bocka ur).
    const wanted = [category, ...(categories ?? [])].filter(
      (c): c is string => Boolean(c) && CATEGORIES.includes(c),
    );
    if (wanted.length > 0) {
      setCheckedCats((prev) => Array.from(new Set([...prev, ...wanted])));
    }

    // Bilderna kunden scannade följer med förfrågan – annars ser vi aldrig delen i mejlet.
    if (aiPhotos && aiPhotos.length > 0) {
      setPhotos((prev) => {
        const seen = new Set(prev.map((p) => p.path));
        return [...prev, ...aiPhotos.filter((p) => !seen.has(p.path))].slice(0, 6);
      });
      setErrors((p) => ({ ...p, photos: "" }));
    }

    // Sammanfattningen är vad kunden får bekräfta i klartext.
    const parts = summary ?? [];
    setAiSummary(parts);
    setDescribeResult(null);

    // Är AI:n osäker (eller hittade ingen giltig kategori) öppnar vi de manuella
    // verktygen automatiskt – med rätt kategorier redan förvalda.
    const weakest = parts.length > 0 ? Math.min(...parts.map((p) => p.confidence)) : 0;
    const uncertain =
      parts.length === 0 ||
      wanted.length === 0 ||
      parts.some((p) => !p.identified) ||
      weakest < 0.7;
    setManualOpen(uncertain);

    // Scroll to form
    requestAnimationFrame(() => {
      document
        .getElementById("reservdel-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  // Kunden kan inte fota: AI:n tolkar fritexten i stället, förväljer kategorier
  // (vilket i sin tur ställer sprängskissen på rätt vy) och kunden bekräftar.
  async function runDescribe() {
    const text = describeText.trim();
    if (text.length < 5) {
      toast.error("Skriv några ord om delen först.");
      return;
    }
    setDescribing(true);
    try {
      const res = await fetch("/api/describe-part", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, brand: brandVal, model: modelVal }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as {
        categories: string[];
        position: string;
        confidence: number;
        summary: string;
        questions: string[];
      };
      const wanted = data.categories.filter((c) => CATEGORIES.includes(c));
      if (wanted.length > 0) {
        setCheckedCats((prev) => Array.from(new Set([...prev, ...wanted])));
      }
      const block = [
        "KUNDEN KUNDE INTE FOTA – beskrivning tolkad av AI:",
        `Kundens egna ord: "${text}"`,
        data.summary ? `Tolkning: ${data.summary}` : "",
        data.position && data.position !== "Okänt" ? `Trolig position: ${data.position}` : "",
        wanted.length > 0 ? `Kategorier: ${wanted.join(", ")}` : "",
        data.questions.length > 0
          ? `Att kontrollera med kunden:\n${data.questions.map((q) => `  - ${q}`).join("\n")}`
          : "",
        "OBS: ingen bild finns – bekräfta varianten med kunden innan beställning.",
      ]
        .filter(Boolean)
        .join("\n");
      setMessageVal((prev) => (prev.trim() ? `${block}\n\n${prev.trim()}` : block));
      setDescribeResult({
        position: data.position,
        summary: data.summary,
        questions: data.questions,
        confidence: data.confidence,
      });
      setAiSummary([]);
      setManualOpen(true);
      toast.success("Tolkat! Bekräfta positionen på skissen nedan.");
      requestAnimationFrame(() => {
        document.getElementById("position-picker")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } catch (err) {
      // Ska aldrig blockera kunden – texten går in i förfrågan ändå.
      setMessageVal((prev) => (prev.trim() ? `${text}\n\n${prev.trim()}` : text));
      setManualOpen(true);
      toast.error(
        err instanceof Error && err.message.length < 160
          ? err.message
          : "Kunde inte tolka texten automatiskt – din beskrivning följer med ändå.",
      );
    } finally {
      setDescribing(false);
    }
  }


  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    trackPartsFunnel("submit_attempt");
    const fd = new FormData(e.currentTarget);

    // Blockera skicka om Kyl / frys är valt utan modell-/serienummer eller fotobevis
    const needsFridgeInfo = checkedCats.includes("Kyl / frys");
    if (needsFridgeInfo && !fridgeSerial.trim() && !fridgePhotoName) {
      setErrors({ fridge: "Fyll i modell-/serienummer eller ladda upp foto på dekalen inne i kylskåpet." });
      toast.error("Kylskåp: vi behöver modell- eller serienummer för att hitta rätt del.");
      requestAnimationFrame(() => {
        document.getElementById("fridge-serial-block")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    // Blockera skicka om en högriskkategori är vald utan beskrivning eller pick+notering.
    // Små beslag (gångjärn, handtag, lås osv) är omöjliga att gissa från översiktsbild.
    const userMsgPreview = String(fd.get("message") ?? "").trim();
    const missingDesc = checkedCats.filter((cat) => {
      if (!HIGH_UNCERTAINTY_CATEGORIES.has(cat)) return false;
      const note = (partNotes[cat] ?? "").trim();
      // Godkänn om kategorinotering finns (>= 5 tecken) ELLER om fritextfältet nedanför
      // innehåller en meningsfull beskrivning (>= 15 tecken – rimlig mening).
      return note.length < 5 && userMsgPreview.length < 15;
    });
    if (missingDesc.length > 0) {
      setErrors({
        highUncertainty: `Beskriv kort din del för: ${missingDesc.join(", ")}. Små beslag/handtag/gångjärn går inte att gissa – lägg till en kort beskrivning (eller foto) så vi hittar rätt.`,
      });
      toast.error("Vi behöver en kort beskrivning för att undvika fel del.");
      requestAnimationFrame(() => {
        document.getElementById(`picker-${missingDesc[0]}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    // Positionskrav: bara när vi INTE har ett foto på delen. Med bild ser vi själva
    // var delen sitter, och då ska kunden inte tvingas peka ut den på skissen också.
    const needPosition = checkedCats.filter((c) => POSITION_REQUIRED_CATEGORIES.has(c));
    if (needPosition.length > 0 && positionPicks.length === 0 && photos.length === 0) {
      setManualOpen(true);
      setErrors({
        position: `Peka ut positionen på skissen för: ${needPosition.join(", ")}. Utan foto behöver vi veta vilket läge det gäller – ett fönster eller en lucka finns i flera.`,
      });
      toast.error("Peka ut var delen sitter på skissen.");
      requestAnimationFrame(() => {
        document.getElementById("position-picker")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    // Fotokrav: alltid minst en tydlig bild. Utan bild kan vi inte godkänna
    // förfrågan – det är bilden som avgör att vi beställer rätt variant.
    if (photos.length === 0) {
      setManualOpen(true);
      setErrors({
        photos:
          "Vi behöver minst en tydlig bild på delen för att kunna godkänna förfrågan. Fota nära, i dagsljus, och ta gärna en extra bild på typskylten/märkningen.",
      });
      toast.error("Ladda upp minst en tydlig bild på delen.");
      requestAnimationFrame(() => {
        document.getElementById("part-photos")?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    // Bygg ihop meddelande med valda referens-brickor + ev. fritext per kategori
    const noteLines: string[] = [];
    for (const cat of checkedCats) {
      const picks = (partSelections[cat] ?? []).filter(Boolean);
      const free = (partNotes[cat] ?? "").trim();
      if (picks.length === 0 && !free) continue;
      const parts: string[] = [];
      if (picks.length) parts.push(picks.join(", "));
      if (free) parts.push(free);
      noteLines.push(`• ${cat}: ${parts.join(" — ")}`);
    }
    const notesBlock = noteLines.join("\n");
    const userMsg = String(fd.get("message") ?? "").trim();
    const fridgeBlock = needsFridgeInfo
      ? [
          "KYLSKÅP – identifiering (kund):",
          fridgeSerial.trim() ? `Modell/serienr: ${fridgeSerial.trim()}` : "",
          fridgePhotoName ? `Foto uppladdat av kund: ${fridgePhotoName} (bifogas separat vid behov)` : "",
        ].filter(Boolean).join("\n")
      : "";
    const positionBlock = formatPositionPicks(positionPicks);
    const combinedMessage = [
      positionBlock ? `POSITION – kunden pekade ut på skissen:\n${positionBlock}` : "",
      fridgeBlock,
      notesBlock ? `Kundens val från referensbilder:\n${notesBlock}` : "",
      userMsg,
    ].filter(Boolean).join("\n\n");

    const raw = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      brand: String(fd.get("brand") ?? ""),
      model: String(fd.get("model") ?? ""),
      year: String(fd.get("year") ?? ""),
      regNumber: String(fd.get("regNumber") ?? "").toUpperCase(),
      chassisNumber: String(fd.get("chassisNumber") ?? ""),
      mileage: String(fd.get("mileage") ?? ""),
      categories: checkedCats,
      urgency: String(fd.get("urgency") ?? ""),
      message: combinedMessage,
      photoPaths: photos.map((p) => p.path),
      // Strukturerat till reservdelsmejlet: exakt vad, var, kundens egna ord,
      // och AI:ns gissningar separat (får aldrig blandas ihop).
      wantedParts: [
        ...aiSummary.filter((p) => p.identified).map((p) => p.label),
        ...checkedCats.flatMap((cat) => (partSelections[cat] ?? []).filter(Boolean)),
      ].filter((v, i, arr) => v && arr.indexOf(v) === i),
      customerNote: [userMsg, notesBlock ? `Kundens val från referensbilder:\n${notesBlock}` : "", fridgeBlock]
        .filter(Boolean)
        .join("\n\n"),
      positions: describePositionPicks(positionPicks),
      aiPosition: describeResult?.position && describeResult.position !== "Okänt" ? describeResult.position : "",
      aiQuestions: describeResult?.questions ?? [],
    };

    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const i of parsed.error.issues) errs[String(i.path[0])] = i.message;
      setErrors(errs);
      toast.error("Kontrollera fälten.");
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const result = await send({ data: parsed.data });
      toast.success("Tack! Vi återkommer inom 24 timmar.");
      trackPartsFunnel("submit_success", false);
      (e.target as HTMLFormElement).reset();
      setCheckedCats([]);
      setPartNotes({});
      setPartSelections({});
      setPositionPicks([]);
      setMessageVal("");
      setFridgeSerial("");
      setFridgePhotoName("");
      setAiSummary([]);
      setManualOpen(false);
      setDescribeText("");
      setDescribeResult(null);
      setPhotos([]);
      setNameVal("");
      setEmailVal("");
      setPhoneVal("");
      setYearVal("");
      setRegVal("");
      setChassisVal("");
      setMileageVal("");
      setUrgencyVal("");
      try { window.localStorage.removeItem(FORM_DRAFT_KEY); } catch {}
      setSuccessRef(result?.reference ?? "RES-—");
      requestAnimationFrame(() => {
        const el = document.getElementById("reservdel-success");
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setSubmitting(false);
    }
  }


  // Referensbilder per märke (utökningsbart – just nu bara Rapido).
  // Dometic kyl/frys sitter i alla märken → visa alltid när "Kyl / frys" väljs.
  const referencesByBrand: Record<string, Record<string, PartTile[]>> = {
    Rapido: RAPIDO_PART_REFERENCES,
  };
  const SHARED_REFS: Record<string, PartTile[]> = {
    "Kyl / frys": RAPIDO_PART_REFERENCES["Kyl / frys"] ?? [],
  };
  const activeRefs: Record<string, PartTile[]> = {
    ...SHARED_REFS,
    ...(referencesByBrand[brandVal] ?? {}),
  };

  function toggleTile(cat: string, label: string) {
    setPartSelections((prev) => {
      const cur = prev[cat] ?? [];
      const next = cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label];
      return { ...prev, [cat]: next };
    });
    if (!checkedCats.includes(cat)) toggleCat(cat);
  }

  return (
    <>
      <PageHeader
        label="Reservdelar husbil & husvagn · Svar inom 24 timmar"
        title={
          <>
            Reservdelar till <span className="font-italic-display text-accent">husbil</span> &amp; <span className="font-italic-display text-accent">husvagn</span>
          </>
        }
        sub="Specialist på reservdelar till Rapido, Dreamer, Itineo, Polar, Dometic och Thetford. Originaldelar, kaross, inredning och tillbehör. Skicka förfrågan — vi återkommer med pris och leveranstid inom 24 timmar. Frakt i hela Sverige."
      />

      {/* STEG 1: VÄLJ MÄRKE (alltid först) */}
      <section className="container-edge pb-8">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-10 elev-shadow">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">1</span>
            <p className="eyebrow !mt-0">Välj märke</p>
          </div>
          <h2 className="mt-3 font-display text-2xl md:text-3xl">
            Vilket <span className="font-italic-display text-accent">märke</span> gäller det?
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Vi behöver veta märket först — då kan AI:n matcha rätt del direkt mot rätt katalog.
          </p>
          <div className="mt-5 grid gap-2.5 grid-cols-2 sm:grid-cols-3 md:grid-cols-6">
            {BRANDS.map((b) => {
              const active = brandVal === b;
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBrandVal(b)}
                  className={`rounded-2xl border-2 px-4 py-4 text-sm font-semibold transition ${
                    active
                      ? "border-accent bg-accent text-accent-foreground shadow-md"
                      : "border-border bg-background hover:border-accent/60 hover:bg-accent/5"
                  }`}
                >
                  {b}
                  {active && <span className="ml-1.5 inline-block">✓</span>}
                </button>
              );
            })}
          </div>
          {brandVal && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <p className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent">
                ✓ Märke valt: {brandVal} — fortsätt nedan
              </p>
              {brandVal === "Rapido" && (
                <p className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                  🚚 Leveranstid Rapido-delar: ca 3 veckor
                </p>
              )}
            </div>
          )}

        </div>
      </section>

      {/* STEG 2: BILDIGENKÄNNING (låst tills märke valt) */}
      <section className="container-edge pb-8">
        {!brandVal ? (
          <div className="rounded-3xl border-2 border-dashed border-border bg-muted/30 p-8 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-muted text-muted-foreground">
              <Package size={22} />
            </div>
            <p className="mt-3 font-display text-lg">Välj märke först ↑</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Så snart du valt märke öppnas bildigenkänningen och formuläret.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">2</span>
              <p className="eyebrow !mt-0">Ladda upp bild på din del — viktigt</p>
            </div>
            <div className="mb-4 rounded-2xl border-2 border-accent/40 bg-accent/10 p-4 md:p-5">
              <p className="font-display text-lg">Ta ett foto på delen du behöver</p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                Samma modell kan ha flera olika varianter av samma del. Bara en beskrivning i text
                räcker oftast inte — då riskerar vi att beställa fel del och du får vänta längre.
                <strong className="text-foreground"> Med en bild hittar vi rätt del direkt.</strong>
              </p>
              <ul className="mt-3 grid gap-1.5 text-sm text-muted-foreground">
                <li>• Fotografera hela delen rakt framifrån, i dagsljus om möjligt</li>
                <li>• Ta gärna en extra bild på hur delen sitter monterad</li>
                <li>• Finns det en etikett/typskylt med siffror — fota den också</li>
              </ul>
            </div>
            <PartAiScanner brand={brandVal} model={modelVal} onApply={applyAiResult} />

            {/* Kan inte fota – AI:n tolkar texten i stället. Diskret, men alltid tillgängligt. */}
            <details className="group mt-4 rounded-2xl border border-border bg-card">
              <summary className="cursor-pointer list-none px-5 py-4 text-sm font-semibold text-foreground marker:hidden">
                <span className="text-accent">Kan du inte fota just nu?</span>{" "}
                <span className="font-normal text-muted-foreground">
                  Beskriv delen i text — vi tolkar den åt dig
                </span>
              </summary>
              <div className="border-t border-border px-5 py-4">
                <p className="text-[13px] leading-relaxed text-muted-foreground">
                  Skriv precis som du skulle sagt det i telefon. Exempel:{" "}
                  <em>"luckan bak till höger, den över hjulet, låset går inte att vrida"</em>.
                </p>
                <textarea
                  rows={3}
                  value={describeText}
                  onChange={(e) => setDescribeText(e.target.value)}
                  placeholder="Beskriv delen och var den sitter…"
                  className="mt-3 w-full resize-none rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/50 focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/15"
                />
                <button
                  type="button"
                  onClick={() => void runDescribe()}
                  disabled={describing || describeText.trim().length < 5}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition hover:opacity-90 disabled:opacity-40"
                >
                  {describing ? "Tolkar beskrivningen…" : "Tolka min beskrivning"}
                </button>
                {describeResult && (
                  <div className="mt-4 rounded-xl border border-accent/40 bg-accent/5 p-4 text-[13px] leading-relaxed">
                    <p className="font-semibold text-foreground">Så här tolkade vi det:</p>
                    {describeResult.summary && <p className="mt-1 text-muted-foreground">{describeResult.summary}</p>}
                    {describeResult.position && describeResult.position !== "Okänt" && (
                      <p className="mt-1 text-muted-foreground">
                        <strong className="text-foreground">Trolig position:</strong> {describeResult.position}
                      </p>
                    )}
                    <p className="mt-2 text-muted-foreground">
                      Bekräfta positionen på skissen längre ner — då slipper vi ringa och fråga.
                    </p>
                  </div>
                )}
              </div>
            </details>



          </>
        )}
      </section>


      <section id="reservdel-success" className="container-edge pb-16 scroll-mt-24">

        {successRef ? (
          <div className="rounded-3xl border-2 border-accent/40 bg-gradient-to-br from-accent/10 via-card to-card p-6 md:p-12 elev-shadow">
            <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-accent text-accent-foreground shadow-lg">
                <Check size={32} strokeWidth={3} />
              </div>
              <p className="eyebrow mt-5 text-accent">Förfrågan mottagen</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl">
                Tack! Vi hör av oss <span className="font-italic-display text-accent">inom 24 timmar</span>.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Din förfrågan är hos vår reservdelsavdelning. Vi kollar delen mot din modell och
                chassinummer, och återkommer på mejl eller telefon med pris och leveranstid.
                En bekräftelse ligger redan i din inkorg — kolla gärna i skräpposten om du inte ser den.
              </p>

              <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-border bg-background px-5 py-3">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Ärendenr</span>
                <span className="font-mono text-base font-bold tracking-wider text-accent">{successRef}</span>
              </div>

              <div className="mt-8 grid w-full gap-3 sm:grid-cols-3">
                <TrustPill icon={<ShieldCheck size={16} />} label="Gratis förfrågan" />
                <TrustPill icon={<Mail size={16} />} label="Svar inom 24h" />
                <TrustPill icon={<Truck size={16} />} label="Frakt i hela Sverige" />
              </div>

              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
                <a
                  href="tel:+46709629743"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-accent bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:bg-accent/10"
                >
                  <Phone size={16} className="text-accent" /> Ring 070-862 97 43
                </a>
                <button
                  type="button"
                  onClick={() => setSuccessRef(null)}
                  className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition hover:opacity-90"
                >
                  Skicka en till förfrågan
                </button>
              </div>
            </div>
          </div>
        ) : brandVal ? (
        <form id="reservdel-form" onSubmit={onSubmit} className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
          {/* Decorative ring strip */}
          <div className="mt-6 flex h-1.5 gap-1 px-6 md:px-10">
            <span className="h-full w-10 rounded-full bg-accent"></span>
            <span className="h-full w-2 rounded-full bg-accent/40"></span>
            <span className="h-full w-2 rounded-full bg-accent/20"></span>
          </div>

          <div className="p-6 md:p-10">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Steg 3 av 3
              </span>
              <span className="h-px flex-1 bg-border"></span>
              <span className="text-[11px] font-medium text-muted-foreground">Reservdelsförfrågan</span>
            </div>
            <h2 className="mt-4 font-display text-3xl leading-tight md:text-4xl">
              Berätta vad du <span className="font-italic-display text-accent">söker</span>.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Endast namn, e-post och reg.nr krävs — resten hjälper oss hitta rätt del snabbare.
            </p>

            {draftSavedAt && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                <Save size={12} /> Utkast sparat {draftSavedAt}
              </p>
            )}

            {prefill && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent">
                ✓ Dina uppgifter är förifyllda från profilen
              </p>
            )}

            {/* Föredrar-du-ringa-strip */}
            <div className="mt-6 flex flex-col items-start gap-3 rounded-2xl border border-dashed border-accent/40 bg-accent/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
                  <Phone size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold">Vill du hellre prata direkt?</p>
                  <p className="text-xs text-muted-foreground">
                    Ring vår reservdelsavdelning — vi hjälper dig hitta rätt del på studs.
                  </p>
                </div>
              </div>
              <a
                href="tel:+46709629743"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition hover:opacity-90"
              >
                <Phone size={14} /> 070-862 97 43
              </a>
            </div>

            {/* Kontakt */}
            <Fieldset num={1} legend="Dina uppgifter">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Namn *" name="name" value={nameVal} onChange={setNameVal} error={errors.name} />
                <Field label="Telefon" name="phone" type="tel" value={phoneVal} onChange={setPhoneVal} error={errors.phone} />
              </div>
              <Field label="E-post *" name="email" type="email" value={emailVal} onChange={setEmailVal} error={errors.email} />
            </Fieldset>


            {/* Fordon */}
            <Fieldset num={2} legend="Fordon">
              <div>
                <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Märke *</label>
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-muted/40 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Valt märke</p>
                    <p className="truncate text-sm font-semibold text-foreground">{brandVal}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBrandVal("");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="shrink-0 text-xs font-semibold text-accent underline-offset-2 hover:underline"
                  >
                    Ändra
                  </button>
                </div>
                <input type="hidden" name="brand" value={brandVal} />
                {errors.brand && <p className="mt-1.5 text-xs text-destructive">{errors.brand}</p>}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Modell" name="model" placeholder="t.ex. 696F" value={modelVal} onChange={setModelVal} />
                <Field label="Årsmodell" name="year" placeholder="t.ex. 2019" value={yearVal} onChange={setYearVal} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Registreringsnummer *" name="regNumber" placeholder="ABC 123" value={regVal} onChange={setRegVal} error={errors.regNumber} />
                <Field label="Chassinummer (VIN)" name="chassisNumber" value={chassisVal} onChange={setChassisVal} />
              </div>
              <Field label="Miltal" name="mileage" placeholder="t.ex. 6 500" value={mileageVal} onChange={setMileageVal} />
            </Fieldset>

            {/* Kategorier */}
            <Fieldset num={3} legend="Vad gäller det?">
              {/* AI-först: bekräftelsekort i klartext. Kryssrutor och skiss visas bara
                  när AI:n är osäker eller kunden själv vill rätta något. */}
              {aiSummary.length > 0 && !showManual && (
                <div className="rounded-2xl border-2 border-emerald-400/50 bg-emerald-50/60 p-4 dark:bg-emerald-500/10 md:p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                    ✓ Klart — vi tolkade din bild
                  </p>
                  <ul className="mt-3 space-y-2.5">
                    {aiSummary.map((p, i) => (
                      <li key={`${p.label}-${i}`} className="text-sm leading-snug">
                        <span className="font-semibold text-foreground">{p.label}</span>
                        <span className="text-muted-foreground">
                          {" — "}
                          {p.category}
                          {p.zoneLabel ? `, ${p.zoneLabel}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
                    Stämmer det? Då behöver du bara fylla i namn, e-post och reg.nr och skicka —
                    vi återkommer med pris och leveranstid.
                  </p>
                  <button
                    type="button"
                    onClick={() => setManualOpen(true)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-border bg-background px-4 py-2 text-[13px] font-semibold text-foreground transition hover:border-accent/60 hover:bg-accent/5"
                  >
                    Nej, jag vill rätta uppgifterna
                  </button>
                </div>
              )}

              {!showManual && aiSummary.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-4 md:p-5">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Fota delen i steg 2 ovan — då fyller vi i kategori, position och beskrivning åt
                    dig. Vill du hellre fylla i själv går det också bra.
                  </p>
                  <button
                    type="button"
                    onClick={() => setManualOpen(true)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-border bg-background px-4 py-2 text-[13px] font-semibold text-foreground transition hover:border-accent/60 hover:bg-accent/5"
                  >
                    Fyll i manuellt i stället
                  </button>
                </div>
              )}

              {showManual && (
              <>
              {aiSummary.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
                  <strong>Vi vill helst att du dubbelkollar.</strong> AI:n är inte helt säker på din
                  del — bekräfta kategorin och peka ut var delen sitter, så beställer vi rätt direkt.
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Kryssa i en eller flera kategorier.
                {brandVal === "Rapido" && (
                  <> När du bockar i en Rapido-kategori dyker <strong className="text-foreground">alla bilder på delar</strong> upp direkt nedanför — klicka på den som liknar din.</>
                )}
              </p>
              <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
                {CATEGORIES.map((cat) => {
                  const active = checkedCats.includes(cat);
                  const selCount = (partSelections[cat] ?? []).length;
                  return (
                    <label
                      key={cat}
                      className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition ${
                        active
                          ? "bg-accent/10 text-foreground"
                          : "hover:bg-accent/5"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleCat(cat)}
                        className="h-4 w-4 rounded accent-[hsl(var(--accent))]"
                      />
                      <span className="flex-1 text-[13px]">{cat}</span>
                      {selCount > 0 && (
                        <span className="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-accent-foreground">
                          {selCount}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Obligatorisk identifiering för kylskåp / frys */}
              {checkedCats.includes("Kyl / frys") && (
                <div
                  id="fridge-serial-block"
                  className="mt-5 rounded-2xl border-2 border-accent/60 bg-accent/5 p-5"
                >
                  <div className="mb-3 flex items-start gap-2">
                    <Snowflake className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                    <div>
                      <h4 className="text-sm font-bold text-foreground">Kylskåp/frys: obligatoriskt att ange modell- eller serienummer</h4>
                      <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                        Numret sitter på en <strong>dekal inne i kylskåpet</strong> — oftast längst ned på innerväggen, i sidan eller under grönsakslådan (t.ex. "RM7291", "RM8500", "PRM8400T5" följt av ett längre nummer). <strong>Utan detta nummer är det omöjligt att beställa rätt del.</strong>
                      </p>
                    </div>
                  </div>

                  <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Modell- / serienummer <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={fridgeSerial}
                    onChange={(e) => { setFridgeSerial(e.target.value); if (errors.fridge) setErrors((p) => ({ ...p, fridge: "" })); }}
                    placeholder="T.ex. RM8500 921086927"
                    className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/50 focus:border-accent focus:ring-2 focus:ring-accent/15"
                  />

                  <div className="my-3 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <div className="h-px flex-1 bg-border" />
                    eller
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Ladda upp foto på dekalen
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const f = e.currentTarget.files?.[0];
                      setFridgePhotoName(f ? f.name : "");
                      if (f && errors.fridge) setErrors((p) => ({ ...p, fridge: "" }));
                    }}
                    className="block w-full text-[13px] text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-accent file:px-4 file:py-2 file:text-[12px] file:font-semibold file:text-accent-foreground hover:file:opacity-90"
                  />
                  {fridgePhotoName && (
                    <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-emerald-600"><Check className="h-3.5 w-3.5" /> {fridgePhotoName}</p>
                  )}

                  {errors.fridge && <p className="mt-2 text-xs font-semibold text-destructive">{errors.fridge}</p>}
                </div>
              )}

              {/* Foton på delen – i praktiken det som avgör att vi hittar rätt variant */}
              {checkedCats.length > 0 && (
                <div id="part-photos" className="mt-5">
                  <PartPhotoUploader
                    photos={photos}
                    onChange={(next) => { setPhotos(next); if (next.length) trackPartsFunnel("photo_uploaded"); if (errors.photos) setErrors((p) => ({ ...p, photos: "" })); }}
                    required
                  />
                  {errors.photos && (
                    <p className="mt-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs font-semibold text-destructive">
                      {errors.photos}
                    </p>
                  )}
                </div>
              )}

              {/* Positionsväljare – kunden pekar ut var delen sitter */}
              {checkedCats.length > 0 && (
                <div id="position-picker" className="mt-5">
                  <PartPositionPicker
                    picks={positionPicks}
                    onChange={setPositionPicks}
                    activeCategories={checkedCats}
                  />

                  {errors.position && (
                    <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                      {errors.position}
                    </p>
                  )}
                </div>
              )}


              {/* Inline delväljare per vald kategori som har referensbilder */}
              {checkedCats.filter((c) => activeRefs[c]).length > 0 && (
                <div className="mt-5 space-y-4">
                  {checkedCats
                    .filter((c) => activeRefs[c])
                    .map((cat) => {
                      const risky = HIGH_UNCERTAINTY_CATEGORIES.has(cat);
                      const noteFilled = (partNotes[cat] ?? "").trim().length >= 5;
                      return (
                        <div key={cat} id={`picker-${cat}`}>
                          {risky && (
                            <div
                              className={`mb-2 flex items-start gap-2 rounded-xl border-2 px-3 py-2 text-xs ${
                                noteFilled
                                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                                  : "border-amber-300 bg-amber-50 text-amber-900"
                              }`}
                            >
                              <span className="mt-0.5">⚠️</span>
                              <p className="leading-snug">
                                <strong>{cat}</strong> – här ingår ofta små beslag (gångjärn, handtag, lås, list) som är svåra att peka ut på översiktsbild.{" "}
                                <strong>Skriv en kort beskrivning</strong> i "Passar ingen bild?"-rutan nedan (t.ex. "Blum-gångjärn till överskåp kök") – annars riskerar vi att beställa fel del.
                              </p>
                            </div>
                          )}
                          <InlinePartPicker
                            category={cat}
                            tiles={activeRefs[cat]}
                            selected={partSelections[cat] ?? []}
                            onToggle={(label) => toggleTile(cat, label)}
                            note={partNotes[cat] ?? ""}
                            onNoteChange={(v) => setPartNotes((p) => ({ ...p, [cat]: v }))}
                          />
                        </div>
                      );
                    })}
                </div>
              )}
              </>
              )}
            </Fieldset>

            {/* Brådska + meddelande */}
            <Fieldset num={4} legend="Beskrivning & tidsram">
              <div>
                <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Beskriv delen (om du vet)</label>
                <textarea
                  name="message"
                  rows={6}
                  value={messageVal}
                  onChange={(e) => setMessageVal(e.target.value)}
                  placeholder="Beskriv delen så gott du kan — men ladda helst upp en bild i steg 2 ovan. Text ensamt gör det svårt att träffa exakt rätt variant."
                  className="w-full resize-none rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/50 focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/15"
                />
                <p className="mt-2 rounded-lg border border-accent/30 bg-accent/10 px-3 py-2 text-xs leading-relaxed text-foreground">
                  <strong>Tips:</strong> bifoga en bild på delen (steg 2 ovan). Utan foto blir det ofta
                  fel variant — med foto kan vi beställa rätt del på en gång.
                </p>
                {errors.message && <p className="mt-1.5 text-xs text-destructive">{errors.message}</p>}

                {errors.highUncertainty && (
                  <p className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                    {errors.highUncertainty}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Hur brådskande?</label>
                <select
                  name="urgency"
                  value={urgencyVal}
                  onChange={(e) => setUrgencyVal(e.target.value)}
                  className="w-full rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground outline-none transition focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/15"
                >
                  <option value="">Välj…</option>
                  {URGENCY.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </Fieldset>

            {/* Trygghetsrad ovanför skicka-knappen */}
            <div className="mt-10 flex flex-wrap gap-2">
              <TrustPill icon={<Check size={12} />} label="Gratis förfrågan" />
              <TrustPill icon={<Mail size={12} />} label="Svar inom 24h" />
              <TrustPill icon={<Truck size={12} />} label="Frakt i hela Sverige" />
              <TrustPill icon={<ShieldCheck size={12} />} label="Ingen förpliktelse" />
            </div>

            <button
              disabled={submitting}
              className="group mt-6 inline-flex w-full items-center justify-center gap-2 btn-shine rounded-2xl bg-primary px-7 py-4 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-lg shadow-primary/20 transition hover:bg-primary/90 disabled:opacity-60"
            >
              <Mail size={16} />
              {submitting ? "Skickar…" : "Skicka förfrågan"}
            </button>

            <p className="mt-4 text-center text-[11px] text-muted-foreground">
              Din förfrågan skickas till{" "}
              <a href="mailto:reservdelar@akhusbilfritid.se" className="text-accent hover:underline">
                reservdelar@akhusbilfritid.se
              </a>
              . Du får en bekräftelse på mejl direkt.
            </p>
          </div>
        </form>

        ) : null}

      </section>






      {/* Info & trygghet — flyttad hit så formuläret ligger överst */}
      <section className="container-edge pb-24">
        <div className="rounded-3xl border border-border bg-muted/30 p-6 md:p-10">
          <p className="eyebrow">Så funkar det</p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl">
            Rätt del — <span className="font-italic-display text-accent">utan gissningar</span>.
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Fyll i formuläret ovan så noga du kan — namn, kontakt, märke och reg.nr räcker för att vi ska
            komma igång. Vi återkommer med pris och leveranstid inom 24 timmar. Vet du inte vad delen heter?
            Använd bildigenkänningen så identifierar vi den åt dig.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Feature icon={<Wrench size={18} />} title="Original & kvalitet" text="Endast Rapido, Dreamer, Itineo, Polar, Dometic och Thetford." />
            <Feature icon={<Truck size={18} />} title="Snabb leverans" text="Delar i lager skickas ofta inom 1–3 arbetsdagar." />
            <Feature icon={<ShieldCheck size={18} />} title="Rätt del direkt" text="Vi dubbelkollar mot chassinummer & modell." />
          </div>
        </div>
      </section>

      {/* SEO-innehåll: märken vi tar hem delar till */}
      <section className="container-edge pb-16">
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow">Märken</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Reservdelar till <span className="font-italic-display text-accent">Rapido, Dreamer, Itineo, Polar, Dometic &amp; Thetford</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Vi fokuserar på reservdelar till de märken vi kan bäst: <strong className="text-foreground">Rapido</strong>,
            {" "}<strong className="text-foreground">Dreamer</strong>, <strong className="text-foreground">Itineo</strong>,
            {" "}<strong className="text-foreground">Polar</strong>, <strong className="text-foreground">Dometic</strong> och <strong className="text-foreground">Thetford</strong>.
            Som Rapido-återförsäljare har vi direktkontakt med tillverkaren och tillgång till original­delar,
            plåtdetaljer och inredning. Behöver du en del till något annat märke? Skicka ändå en förfrågan —
            vi har bred branschkontakt och tar hem det mesta.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Rapido", "Dreamer", "Itineo", "Polar", "Dometic", "Thetford"].map((b) => (
              <span key={b} className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SEO-innehåll: vanliga reservdelar */}
      <section className="container-edge pb-16">
        <div className="mx-auto max-w-5xl">
          <p className="eyebrow">Vanliga reservdelar</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Vad vi <span className="font-italic-display text-accent">oftast</span> beställer hem
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
            Slitage, ålder och vinterkyla sätter sina spår. Här är de reservdelar vi hjälper husbils- och
            husvagnsägare med varje vecka — men vi tar självklart hem allt annat också, från en liten
            packning till en hel takventilation.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            <PartCard
              icon={<Snowflake size={18} />}
              title="Kylskåp & frys"
              text="Dometic och Thetford absorptionskyl — termostat, brännare, gaspatron, kondensor, dörrpackning och kylgaller."
            />
            <PartCard
              icon={<Flame size={18} />}
              title="Värmesystem"
              text="Truma Combi & S-serie, Alde vattenburen värme — brännare, tändare, cirkulations­pump, expansionskärl och styrkort."
            />
            <PartCard
              icon={<Droplets size={18} />}
              title="Vatten & toalett"
              text="Whale-pumpar, Thetford och Dometic kassett-toaletter — bänkskiva, tätning, spolpump, hjul och avlopps­kranar."
            />
            <PartCard
              icon={<Zap size={18} />}
              title="El & solpanel"
              text="AGM- och litium­batterier, laddningsregulatorer, växelriktare, solpaneler och 12V-belysning."
            />
            <PartCard
              icon={<Sun size={18} />}
              title="Markiser & fönster"
              text="Fiamma F45 / F65, Omnistor — dukar, armar, ändlock samt Seitz-fönster, gasfjädrar och myggnät."
            />
            <PartCard
              icon={<DoorOpen size={18} />}
              title="Dörr, lås & kaross"
              text="Dörrhandtag, lås­cylindrar, gångjärn, list­tätning, takluckor (Mini Heki, Midi Heki) och plåt­detaljer."
            />
          </div>
        </div>
      </section>

      {/* SEO-djup: artikelnummer, priser, leveranstider, felsökning */}
      <section className="container-edge pb-16">
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow">Så hittar du rätt del</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Var sitter <span className="font-italic-display text-accent">artikelnumret</span>?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Nio av tio felleveranser beror på att delen beställts efter beskrivning i stället för efter
            artikelnummer. Alla vitvaror, värmare och pumpar i en husbil har en dekal med modell- och
            serienummer — hittar du den slipper du både returer och väntetid. Så här letar du per system:
          </p>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">System</th>
                  <th className="px-4 py-3 font-semibold">Var dekalen sitter</th>
                  <th className="px-4 py-3 font-semibold">Ser ut som</th>
                </tr>
              </thead>
              <tbody className="[&>tr:not(:last-child)]:border-b [&>tr]:border-border">
                <tr>
                  <td className="px-4 py-3 font-medium">Dometic kyl/frys</td>
                  <td className="px-4 py-3 text-muted-foreground">Innerväggen i kylen, ofta bakom grönsakslådan</td>
                  <td className="px-4 py-3 text-muted-foreground">RM 8551 / N30 + 9 siffror</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Truma Combi</td>
                  <td className="px-4 py-3 text-muted-foreground">Ovansidan av pannan, under sittbänk eller i garage</td>
                  <td className="px-4 py-3 text-muted-foreground">Combi 4 / 6 E + serienr</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Alde värmepanna</td>
                  <td className="px-4 py-3 text-muted-foreground">Fronten på pannan, ofta i garderobsbotten</td>
                  <td className="px-4 py-3 text-muted-foreground">Compact 3020 / 3010</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Thetford toalett</td>
                  <td className="px-4 py-3 text-muted-foreground">Kassetten och luckans insida</td>
                  <td className="px-4 py-3 text-muted-foreground">C200 CW / C260 / C400</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Fiamma markis</td>
                  <td className="px-4 py-3 text-muted-foreground">Ändlocket i höger sida, kassettens undersida</td>
                  <td className="px-4 py-3 text-muted-foreground">F45 S / F45 L / F65 + längd i cm</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Seitz/Dometic fönster</td>
                  <td className="px-4 py-3 text-muted-foreground">Nedre hörnet på ramen, insidan</td>
                  <td className="px-4 py-3 text-muted-foreground">S4 / S7 + mått i mm (t.ex. 900×450)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Kaross & inredning</td>
                  <td className="px-4 py-3 text-muted-foreground">Inget nummer — kräver chassinr (VIN) + årsmodell</td>
                  <td className="px-4 py-3 text-muted-foreground">VF3… (17 tecken) på förarsidan</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Har du bara reg.nr räcker det ofta — vi slår upp chassinummer, årsmodell och basfordon och matchar
            mot rätt katalog. Är det en liten detalj som gångjärn, handtag eller lucka: fota på nära håll och
            lägg gärna en tumme eller ett måttband i bild, så ser vi storleken direkt.
          </p>
        </div>
      </section>

      <section className="container-edge pb-16">
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow">Prisbild &amp; leveranstid</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Vad kostar en <span className="font-italic-display text-accent">reservdel</span> — och hur snabbt kommer den?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Intervallen nedan är typiska nivåer från våra egna förfrågningar det senaste året (delpris exkl.
            montering, inkl. moms). De ska ses som en storleksordning inför budget — exakt pris får du i svaret
            på din förfrågan inom 24 timmar på vardagar.
          </p>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Del</th>
                  <th className="px-4 py-3 font-semibold">Typiskt pris</th>
                  <th className="px-4 py-3 font-semibold">Leveranstid</th>
                </tr>
              </thead>
              <tbody className="[&>tr:not(:last-child)]:border-b [&>tr]:border-border">
                {[
                  ["Dörrpackning kylskåp", "450–1 200 kr", "3–7 dagar"],
                  ["Termostat / styrkort kyl", "1 200–3 500 kr", "5–10 dagar"],
                  ["Truma brännare eller tändelektrod", "700–2 400 kr", "3–7 dagar"],
                  ["Alde cirkulationspump", "1 900–3 200 kr", "3–7 dagar"],
                  ["Whale/Shurflo tryckvattenpump", "900–2 200 kr", "1–3 dagar"],
                  ["Thetford spolpump + tätningssats", "400–1 100 kr", "1–3 dagar"],
                  ["Markisduk Fiamma F45 (3,5 m)", "3 500–6 500 kr", "7–14 dagar"],
                  ["Taklucka Mini/Midi Heki komplett", "2 400–5 900 kr", "5–10 dagar"],
                  ["Seitz-fönster på mått", "4 500–11 000 kr", "10–20 dagar"],
                  ["Gasfjäder / gångjärn / handtag", "150–800 kr", "1–5 dagar"],
                  ["Servicebatteri AGM 100 Ah", "2 200–3 400 kr", "Lager–3 dagar"],
                  ["Litiumbatteri 100 Ah med BMS", "7 900–14 500 kr", "3–10 dagar"],
                ].map(([a, b, c]) => (
                  <tr key={a}>
                    <td className="px-4 py-3 font-medium">{a}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Karossdelar och lackade detaljer ligger nästan alltid högst i både pris och ledtid, eftersom de
            beställs mot chassinummer från fabrik. Under semesterveckorna i juli–augusti bör du räkna med
            några dagar extra på franska och tyska leverantörer.
          </p>
        </div>
      </section>

      <section className="container-edge pb-16">
        <div className="mx-auto max-w-4xl">
          <p className="eyebrow">Felsökning först</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">
            Innan du köper en ny del — <span className="font-italic-display text-accent">kolla det här</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            En stor del av de förfrågningar vi får löses utan ny del. Verkstadens vanligaste fynd, i den
            ordning vi själva felsöker:
          </p>
          <ul className="mt-6 space-y-3">
            {[
              ["Kylen kyler inte på gas", "Nio av tio gånger är det smutsig brännare eller för lågt gastryck — rengör brännaren och testa regulatorn innan du byter termostat."],
              ["Kylen kyler inte på 12 V", "12 V-elementet får bara ström när motorn går på de flesta modeller. Mät över elementet (ca 10–12 A) innan du dömer ut det."],
              ["Truma tänder inte", "Kontrollera gasol i flaskan, vintergasol vid minusgrader (propan, inte butan) och att tändelektrodens gnistgap är rent."],
              ["Vattenpumpen går men inget kommer", "Luft i systemet eller igensatt filter i färskvattentanken — inte pumpen. Töm, fyll och lufta först."],
              ["Markisen går trögt", "Rengör och silikonspreja skenan; trög arm beror oftare på smuts än på trasig gasfjäder."],
              ["Fukt i tak eller list", "Byt aldrig list innan en fuktmätning gjorts — annars tätar du över ett aktivt läckage."],
            ].map(([t, d]) => (
              <li key={t} className="rounded-2xl border border-border bg-card p-4">
                <p className="font-medium text-sm">{t}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{d}</p>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-muted-foreground">
            Vill du att vi gör jobbet? <a href="/boka-service" className="underline decoration-accent/60 underline-offset-4">Boka verkstad i Kalix</a>
            {" "}— vi felsöker, beställer rätt del och monterar den. Misstänker du läckage, börja med{" "}
            <a href="/fukttest-husbil" className="underline decoration-accent/60 underline-offset-4">fukttest</a>.
          </p>
        </div>
      </section>

      <section className="container-edge pb-12">
        <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-secondary/40 p-6 md:p-8">
          <h2 className="font-display text-2xl md:text-3xl">Reservdelar per märke</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Typskyltens placering, vanliga delar med prisspann och kända svagheter — märke för märke.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {PARTS_BRANDS.map((b) => (
              <a
                key={b.slug}
                href={`/reservdelar/${b.slug}`}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:bg-secondary"
              >
                {b.name} reservdelar
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge pb-12">
        <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-card p-6 md:p-8">
          <h2 className="font-display text-2xl md:text-3xl">Reservdelar per kategori</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Vad som går sönder, vad vi behöver veta för att beställa rätt och prisspann — kategori för
            kategori.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {PARTS_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/reservdelar/kategori/${c.slug}`}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:bg-secondary"
              >
                {c.name}
              </a>
            ))}
          </div>
        </div>
      </section>



      <div className="container-edge pb-16">
        <div className="mx-auto max-w-4xl">
          <RelatedLinks
            keyId="reservdelar"
            text="reservdel del pump lucka fönster kylskåp gasol service verkstad fukt besiktning solceller batteri markis"
            currentHref="/reservdelar"
            max={6}
          />
        </div>
      </div>



      {/* FAQ */}
      <section className="container-edge pb-24">
        <div className="mx-auto max-w-3xl">
          <p className="eyebrow text-center">Frågor & svar</p>
          <h2 className="mt-3 text-center font-display text-3xl md:text-4xl">
            Vanliga frågor om <span className="font-italic-display text-accent">reservdelar</span>
          </h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-border bg-card p-6 md:p-8 text-center soft-shadow">
            <p className="font-display text-2xl">
              Hittar du inte svaret? <span className="font-italic-display text-accent">Ring oss.</span>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Vi hjälper dig identifiera rätt del och ger dig ett rakt pris — samma dag.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="tel:0708629743"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                <Phone size={16} /> 070-862 97 43
              </a>
              <a
                href="#reservdel-form"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-medium transition hover:border-accent"
              >
                <Package size={16} /> Skicka förfrågan
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const FAQS: { q: string; a: string }[] = [
  {
    q: "Vilka märken kan ni beställa reservdelar till?",
    a: "Just nu tar vi endast emot förfrågningar för Rapido, Dreamer, Itineo, Polar, Dometic och Thetford. Andra märken kan vi tyvärr inte hantera för tillfället.",
  },
  {
    q: "Hur lång är leveranstiden på reservdelar?",
    a: "Delar i lager skickas ofta inom 1–3 arbetsdagar. Beställningsvaror har normalt 3–7 arbetsdagars leveranstid. Vi svarar alltid på din förfrågan inom 24 timmar på vardagar med pris och exakt leveranstid.",
  },
  {
    q: "Kan ni hitta delar till äldre husbilar och husvagnar?",
    a: "Ja. Med reg.nr eller chassinummer (VIN) kan vi identifiera rätt del även till äldre modeller. Om originaldelen inte längre tillverkas hjälper vi dig hitta en kompatibel ersättare.",
  },
  {
    q: "Skickar ni reservdelar i hela Sverige?",
    a: "Ja, vi skickar reservdelar till husbil och husvagn över hela Sverige. Du kan också hämta delarna på plats i vår butik i Kalix, Norrbotten.",
  },
  {
    q: "Vad kostar det att göra en förfrågan?",
    a: "Förfrågan är helt gratis och utan förpliktelser. Du får pris och leveranstid från oss innan du bestämmer dig.",
  },
  {
    q: "Kan jag skicka en bild om jag inte vet vad delen heter?",
    a: "Ja. Använd vår bildigenkänning högst upp på sidan – ladda upp en bild på delen så identifierar vi vad det är och lägger till rätt kategori i förfrågan.",
  },
  {
    q: "Behöver jag registreringsnummer för att beställa?",
    a: "Reg.nr eller chassinummer gör att vi träffar rätt del direkt och undviker returer. Har du inget till hands – hör av dig ändå så löser vi det tillsammans.",
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="group rounded-2xl border border-border bg-card p-5 transition open:border-accent/60 open:shadow-sm">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
        <span className="font-medium text-base">{q}</span>
        <ChevronDown
          size={20}
          className="mt-0.5 shrink-0 text-muted-foreground transition group-open:rotate-180 group-open:text-accent"
        />
      </summary>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{a}</p>
    </details>
  );
}

function PartCard({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 soft-shadow transition hover:border-accent/60">
      <div className="grid h-10 w-10 place-items-center rounded-full bg-accent/10 text-accent">
        {icon}
      </div>
      <h3 className="mt-3 font-display text-lg">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{text}</p>
    </div>
  );
}

function Fieldset({ legend, num, children }: { legend: string; num?: number | string; children: React.ReactNode }) {
  return (
    <fieldset className="mt-10 space-y-5">
      <legend className="mb-4 flex items-center gap-3 p-0">
        {num !== undefined && (
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground text-[11px] font-bold text-background">
            {num}
          </span>
        )}
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground">
          {legend}
        </span>
      </legend>
      {children}
    </fieldset>
  );
}


function Field({
  label,
  name,
  type = "text",
  error,
  placeholder,
  value,
  onChange,
  defaultValue,
}: {
  label: string;
  name: string;
  type?: string;
  error?: string;
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
  defaultValue?: string;
}) {
  const controlled = value !== undefined && onChange !== undefined;
  return (
    <div>
      <label className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        {...(controlled
          ? { value, onChange: (e) => onChange!(e.target.value) }
          : { defaultValue })}
        className="w-full rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground/50 focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/15"
      />
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}


function Feature({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 soft-shadow">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
        {icon}
      </div>
      <div>
        <p className="font-medium text-sm">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

function TrustPill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
        {icon}
      </span>
      <span className="text-xs font-semibold text-foreground">{label}</span>
    </div>
  );
}


